/**
 * The favicon policy shared by the client and the server.
 *
 * Favicons are served by Amoreg's own server (`/api/amoreg/favicon?host=…`),
 * never by a public favicon service: asking `google.com/s2/favicons` for an
 * icon would tell a third party, from the analyst's browser, the hostname of
 * every page an agent read for a tenant. This module decides which host may be
 * asked for at all; the server decides what is safe to serve back.
 */
import { isPublicFaviconHost } from "./hostClassification.ts";

export { isPublicFaviconHost };

/** The first-party route that serves a site's favicon. */
export const FAVICON_ROUTE_PATH = "/api/amoreg/favicon";

/**
 * The public hostname whose favicon may be fetched for `rawUrl`, or `null`
 * when the location is unusable (not http(s), unparseable) or names a
 * private, reserved or internal host, which must not leave the browser.
 *
 * Only the hostname is kept: no port, path, query or credentials.
 */
export function faviconHostForOrigin(rawUrl: string | null | undefined): string | null {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (!url.hostname) return null;
    const hostname = url.hostname.toLowerCase();
    if (!isPublicFaviconHost(hostname)) return null;
    // A single trailing dot is the same name; drop it so the server caches one
    // entry per site. Repeated dots were already refused above.
    return hostname.replace(/\.$/u, "");
  } catch {
    return null;
  }
}
